import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import { Plus, Store } from "lucide-react";
import { useSelector } from "react-redux";
import {
  selectCurrentUser,
  selectRefreshToken,
  selectToken,
} from "../../features/authSlice";
import supabase from "../../helper/supabaseClient";
import { redirectToDashboard } from "../../utils/dashboardRedirect";

// Define a type for site data
interface Site {
  id: string;
  name: string;
  slug: string;
  business_image?: string;
  is_subscribed: boolean;
  is_free_trial: boolean;
}
export const getSites = async (id: string) => {
  const result = await supabase
    .from("BusinessSite")
    .select("*")
    .eq("owner", id)
    .eq("site_type", "ecommerce");

  return result;
};

const UserStores = () => {
  const navigate = useNavigate();
  const token = useSelector(selectToken);
  const refreshToken = useSelector(selectRefreshToken);
  const user = useSelector(selectCurrentUser);
  const [sites, setSites] = useState<Site[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>();
  const fetchUserSites = async (id: string) => {
    setIsLoading(true);
    const { data, error } = await getSites(id);
    if (error) {
      setError(error);
      setIsLoading(false);
      return;
    }
    if (data) {
      setSites(data as Site[]);
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (!user) return;
    fetchUserSites(user.id);
  }, [user]);


  useEffect(() => {
    // If there's no token, redirect to login
    if (!token) {
      navigate("/login");
    }
  }, [token, navigate]);

  async function goToDashboard(slug: string) {
    await redirectToDashboard({
      slug,
      fallbackAccessToken: token,
      fallbackRefreshToken: refreshToken,
    });
  }

  return (
    <div>
      <Navbar />
      <div className="pt-10 px-4 h-[81vh] sm:p-14 flex items-start md:items-center justify-center landing-bg bg-cover bg-no-repeat bg-center ">
        <div className="w-full md:w-fit flex items-center justify-center flex-col bg-white ">
          <div className="sm:p-10 rounded-2xl sm:border border-[#7E57C233] w-[100%] sm:w-[95%] md:w-fit flex items-center justify-between gap-20">
            <div className="flex flex-col md:max-h-[60vh] gap-4 items-start justify-start w-[500px] lg:w-[600px]">
              <div className="flex items-center flex-col justify-center gap-2 w-full">
                {/* {user?.profile_picture && (
                  <img
                    src={user.profile_picture}
                    alt="Profile"
                    className="h-12 w-12 rounded-full object-cover"
                  />
                )} */}
                {
                  <div className="h-12 w-12 rounded-full bg-[#7E57C2] flex items-center justify-center text-white">
                    {user?.first_name?.charAt(0) || "U"}
                  </div>
                }
                <h2 className="text-center">
                  Welcome back, {user?.first_name || "User"}
                </h2>
              </div>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-start justify-center">
                  <h4 className="text-[#667085]">Your stores</h4>
                </div>
                <a
                  href="/add-store"
                  className="px-3 py-2 flex items-center justify-center gap-1 rounded-lg bg-[#7F56D9] text-white"
                >
                  <Plus size={18} />
                  Add Business
                </a>
              </div>

              {isLoading && (
                <div className="w-full flex justify-center py-8">
                  <p>Loading your stores...</p>
                </div>
              )}

              {error && (
                <div className="w-full flex justify-center py-8 text-red-500">
                  <p>Failed to load your stores. Please try again.</p>
                  <button
                    onClick={() => fetchUserSites(user?.id!)}
                    className="ml-2 underline text-[#7E57C2]"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!isLoading && !error && sites.length === 0 && (
                <div className="w-full flex flex-col items-center justify-center gap-4 py-8">
                  <Store size={48} color="#7E57C2" />
                  <p>You don't have any stores yet.</p>
                  <a
                    href="/add-store"
                    className="px-4 py-2 bg-[#7F56D9] text-white rounded-lg flex items-center gap-2"
                  >
                    <Plus size={16} /> Create your first store
                  </a>
                </div>
              )}
              <div className="w-full overflow-y-auto no-scrollbar">
                {!isLoading &&
                  !error &&
                  sites.length > 0 &&
                  sites.map((site: Site) => (
                    <div
                      key={site.name}
                      className="flex  items-center justify-between w-full cursor-pointer  border-b border-dashed border-[#D2D2D2] py-4"
                      onClick={() => goToDashboard(site.slug)}
                    >
                      <div className="flex items-center justify-center gap-3">
                        <div className="flex items-center justify-center">
                          {site?.business_image ? (
                            <img
                              src={site.business_image}
                              alt={site.name}
                              className="h-12 w-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-full bg-[#F9FAFB] flex items-center justify-center">
                              <Store size={24} color="#7E57C2" />
                            </div>
                          )}
                        </div>
                        <div className="flex items-start justify-center flex-col ">
                          <p>{site.name}</p>
                          <span className="text-[#667085]">
                            {site.slug}.jellosite.store
                          </span>
                        </div>
                      </div>
                      <p
                        className={`px-3 py-1 rounded-2xl flex items-center justify-center gap-2
                                  ${
                                    site.is_subscribed || site.is_free_trial
                                      ? "bg-[#DCFCE7] text-[#14532D]"
                                      : "bg-[#FEF2F2] text-[#991B1B]"
                                  }`}
                      >
                        {site.is_subscribed || site.is_free_trial
                          ? "Active"
                          : "Inactive"}
                      </p>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserStores;
